/**
 * StatsSummary — compact 2x2 stats grid with flash-on-change animation.
 */

import { useState, useRef, useEffect } from 'react';
import { Hash, Wrench, Zap, Clock } from 'lucide-react';
import { cn } from '../../../utils';
import { Text } from '../../atoms/Typography';
import { formatDuration, formatTokens } from './utils';
import type { ExecutionStats } from './types';

/** Stat value with flash-on-change animation */
function AnimatedStatValue({ value }: { value: string | number }) {
  const [flash, setFlash] = useState(false);
  const prevRef = useRef(value);

  useEffect(() => {
    if (prevRef.current !== value) {
      prevRef.current = value;
      setFlash(true);
      const timer = setTimeout(() => setFlash(false), 600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [value]);

  return (
    <Text weight="semibold" className="text-sm leading-tight">
      <span className={cn('transition-colors duration-500', flash && 'text-blue-500 dark:text-blue-400')}>
        {value}
      </span>
    </Text>
  );
}

export interface StatsSummaryProps {
  stats: ExecutionStats;
  isExecuting: boolean;
  /** Override labels — keys: turns, tools, tokens, duration */
  labels?: Partial<Record<'turns' | 'tools' | 'tokens' | 'duration', string>>;
  /** Override the card background classes (default: bg-slate-50 dark:bg-slate-800/50) */
  cardClassName?: string;
}

export function StatsSummary({ stats, isExecuting: _isExecuting, labels, cardClassName }: StatsSummaryProps) {
  const items = [
    {
      icon: Hash,
      label: labels?.turns ?? 'Turns',
      value: stats.turnCount,
    },
    {
      icon: Wrench,
      label: labels?.tools ?? 'Tools',
      value: stats.toolCallCount,
    },
    {
      icon: Zap,
      label: labels?.tokens ?? 'Tokens',
      value: formatTokens(stats.tokenUsage.totalTokens),
    },
    {
      icon: Clock,
      label: labels?.duration ?? 'Duration',
      value: formatDuration(stats.totalDurationMs),
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map(({ icon: Icon, label, value }) => (
        <div
          key={label}
          className={cn('flex items-center gap-2.5 rounded-lg px-3 py-2.5', cardClassName ?? 'bg-slate-50 dark:bg-slate-800/50')}
        >
          <Icon className="h-4 w-4 opacity-30 flex-shrink-0" />
          <div className="min-w-0">
            <Text size="xs" color="muted" className="leading-none">{label}</Text>
            <AnimatedStatValue value={value} />
          </div>
        </div>
      ))}
    </div>
  );
}
